import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { FileXls, FunnelSimple } from '@phosphor-icons/react';
import { formatDate } from '../utils/dateFormat';
import { DaywiseProductivity } from '../components/reports/DaywiseProductivity';


function formatDuration(minutes) {
  if (!minutes) return '--';
  const hrs = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (hrs === 0) return `${mins}m`;
  return `${hrs}h ${mins}m`;
}

function formatDateTime(isoString) {
  if (!isoString) return '--';
  const d = new Date(isoString);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${day}-${month}-${year} ${hours}:${minutes}`;
}

const STATUS_LABELS = {
  production_completed: 'Production Completed',
  scheduled_for_dispatch: 'Scheduled for Dispatch',
  partially_dispatched: 'Partially Dispatched',
  dispatched: 'Dispatched'
};

export function ProductionReports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    jo_number: '', customer_name: '', customer_mobile: '', date_from: '', date_to: ''
  });

  const updateFilter = (key, val) => setFilters(prev => ({ ...prev, [key]: val }));

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v.trim()) params.append(k, v.trim()); });
      const { data: res } = await api.get(`/api/production/completed-report?${params}`);
      setData(res);
    } catch {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const exportCsv = () => {
    if (!data?.jobs?.length) return;
    const headers = ['JO Number', 'Customer Name', 'Customer Mobile', 'Work Location', 'JO Date', 'Delivery Date', 'No of Windows', 'Total Sqft', 'Production Start', 'Production End', 'Total Time (min)', 'Status'];
    const rows = data.jobs.map(jo => [
      jo.jo_number, jo.customer_name, jo.customer_mobile, jo.work_location || '',
      formatDate(jo.jo_date) || jo.jo_date, formatDate(jo.delivery_date) || jo.delivery_date,
      jo.no_of_windows, jo.total_sqft,
      formatDateTime(jo.production_start_at), formatDateTime(jo.production_end_at),
      jo.production_duration_minutes || '', STATUS_LABELS[jo.status] || jo.status
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `production_completed_report_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  return (
    <Layout>
      <div className="space-y-6 pb-20">
        <div>
          <h1 className="text-4xl font-light tracking-tight text-zinc-950">Production Reports</h1>
          <p className="text-sm text-zinc-500 mt-2">Completed production job orders and productivity</p>
        </div>

        {/* Day-wise output chart */}
        <DaywiseProductivity />

        {/* Filters */}
        <Card className="border-zinc-300 rounded-sm shadow-sm">
          <CardHeader className="border-b border-zinc-300 py-3 px-6">
            <CardTitle className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-2">
              <FunnelSimple size={16} weight="bold" /> Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">JO Number</Label>
                <Input data-testid="filter-jo-number" placeholder="Search JO..." value={filters.jo_number} onChange={e => updateFilter('jo_number', e.target.value)} className="h-10" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Customer Name</Label>
                <Input data-testid="filter-customer-name" placeholder="Search customer..." value={filters.customer_name} onChange={e => updateFilter('customer_name', e.target.value)} className="h-10" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Customer Mobile</Label>
                <Input data-testid="filter-customer-mobile" placeholder="Search mobile..." value={filters.customer_mobile} onChange={e => updateFilter('customer_mobile', e.target.value)} className="h-10" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Delivery From</Label>
                <Input data-testid="filter-date-from" type="date" value={filters.date_from} onChange={e => updateFilter('date_from', e.target.value)} className="h-10" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Delivery To</Label>
                <Input data-testid="filter-date-to" type="date" value={filters.date_to} onChange={e => updateFilter('date_to', e.target.value)} className="h-10" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Stats */}
        {data && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-zinc-300 rounded-sm shadow-sm">
              <CardContent className="p-4">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Completed</p>
                <p className="text-2xl font-light text-zinc-950">{data.totals.total_jobs}</p>
              </CardContent>
            </Card>
            <Card className="border-zinc-300 rounded-sm shadow-sm">
              <CardContent className="p-4">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Windows</p>
                <p className="text-2xl font-light text-zinc-950">{data.totals.total_windows}</p>
              </CardContent>
            </Card>
            <Card className="border-zinc-300 rounded-sm shadow-sm">
              <CardContent className="p-4">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Sqft</p>
                <p className="text-2xl font-light text-zinc-950">{data.totals.total_sqft}</p>
              </CardContent>
            </Card>
            <Card className="border-zinc-300 rounded-sm shadow-sm">
              <CardContent className="p-4">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Hours</p>
                <p className="text-2xl font-light text-zinc-950">{data.totals.total_hours}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Export Button */}
        {data?.jobs?.length > 0 && (
          <div className="flex justify-end">
            <Button data-testid="export-csv-button" onClick={exportCsv} variant="outline" className="h-10">
              <FileXls size={18} weight="bold" className="mr-2" />
              Export CSV
            </Button>
          </div>
        )}

        {/* Results Table */}
        {loading && <div className="text-center py-12 text-zinc-500">Loading report...</div>}
        {!loading && data?.jobs?.length > 0 && (
          <Card className="border-zinc-300 rounded-sm shadow-sm">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-200 bg-zinc-50">
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">JO No</th>
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Customer</th>
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Mobile</th>
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Location</th>
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Delivery</th>
                      <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Windows</th>
                      <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Sqft</th>
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Start</th>
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">End</th>
                      <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Time</th>
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.jobs.map(jo => (
                      <tr key={jo.jo_number} data-testid={`report-row-${jo.jo_number}`} className="border-b border-zinc-100 hover:bg-zinc-50">
                        <td className="p-3 font-bold text-zinc-950">{jo.jo_number}</td>
                        <td className="p-3 text-zinc-700">{jo.customer_name}</td>
                        <td className="p-3 text-zinc-700">{jo.customer_mobile}</td>
                        <td className="p-3 text-zinc-700">{jo.work_location || '--'}</td>
                        <td className="p-3 text-zinc-700">{formatDate(jo.delivery_date) || jo.delivery_date}</td>
                        <td className="p-3 text-center text-zinc-700">{jo.no_of_windows}</td>
                        <td className="p-3 text-center text-zinc-700">{jo.total_sqft}</td>
                        <td className="p-3 text-zinc-700 text-xs">{formatDateTime(jo.production_start_at)}</td>
                        <td className="p-3 text-zinc-700 text-xs">{formatDateTime(jo.production_end_at)}</td>
                        <td className="p-3 text-center text-purple-700 font-bold">{formatDuration(jo.production_duration_minutes)}</td>
                        <td className="p-3">
                          <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs">
                            {STATUS_LABELS[jo.status] || jo.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
        {!loading && data && data.jobs.length === 0 && (
          <Card className="border-zinc-300 rounded-sm shadow-sm">
            <CardContent className="p-12 text-center text-zinc-500">No completed production records found</CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
